import { addBreadcrumb } from '../breadcrumbs/breadcrumbs';

export interface SelectedItemInfo {
  itemId: string;
  subject: string;
  conversationId: string;
  internetMessageId?: string;
  itemMode?: string;
  itemType?: string;
  hasAttachment?: boolean;
  /** Sender display name, when known (single open mail only). */
  fromName?: string;
  /** Sender address, when known (single open mail only). */
  fromAddress?: string;
  /** ISO timestamp the mail was sent/received, when known. */
  sentAt?: string;
}

type Listener = (items: SelectedItemInfo[]) => void;
const listeners = new Set<Listener>();
let handlerInstalled = false;

function notify(items: SelectedItemInfo[]): void {
  for (const l of listeners) l(items);
}

function mailboxApi(): typeof Office.context.mailbox | null {
  const office = (globalThis as { Office?: typeof Office }).Office;
  return office?.context?.mailbox ?? null;
}

export async function getSelectedItems(): Promise<SelectedItemInfo[]> {
  const mailbox = mailboxApi();
  if (!mailbox?.getSelectedItemsAsync) return [];
  return new Promise((resolve, reject) => {
    mailbox.getSelectedItemsAsync((result) => {
      if (result.status === Office.AsyncResultStatus.Succeeded) {
        const items = (result.value ?? []) as unknown as SelectedItemInfo[];
        resolve(items);
      } else {
        reject(result.error);
      }
    });
  });
}

// The single message currently shown in the reading pane, as a SelectedItemInfo.
// getSelectedItemsAsync (multi-select) can return [] when just one mail is open
// (it needs the reading pane on + a list selection), so we fall back to this so
// the "Ranger ici" button still shows for the common one-mail-open case.
export function getCurrentItemAsSelected(): SelectedItemInfo | null {
  const item = mailboxApi()?.item as unknown as
    | {
        itemId?: string;
        subject?: string;
        conversationId?: string;
        internetMessageId?: string;
        itemType?: string;
        from?: { displayName?: string; emailAddress?: string } | null;
        dateTimeCreated?: Date | string | null;
      }
    | null
    | undefined;
  if (!item?.itemId) return null;
  let sentAt: string | undefined;
  if (item.dateTimeCreated) {
    const d = new Date(item.dateTimeCreated);
    if (!Number.isNaN(d.getTime())) sentAt = d.toISOString();
  }
  return {
    itemId: item.itemId,
    subject: item.subject ?? '',
    conversationId: item.conversationId ?? '',
    internetMessageId: item.internetMessageId,
    itemMode: 'read',
    itemType: item.itemType ?? 'message',
    fromName: item.from?.displayName ?? undefined,
    fromAddress: item.from?.emailAddress ?? undefined,
    sentAt,
  };
}

// Prefer a real (multi-)selection; fall back to the currently open message.
export async function getEffectiveSelection(): Promise<SelectedItemInfo[]> {
  // Pas d'initialisation ici : les deux branches ci-dessous écrivent la valeur,
  // et un `= []` mort déclenche `no-useless-assignment`.
  let selected: SelectedItemInfo[];
  try {
    selected = await getSelectedItems();
  } catch {
    selected = [];
  }
  if (selected.length > 0) return selected;
  const current = getCurrentItemAsSelected();
  return current ? [current] : [];
}

export function onSelectionChanged(listener: Listener): () => void {
  listeners.add(listener);
  const mailbox = mailboxApi();
  // In some hosts/contexts (or when the add-in is loaded outside Office, e.g.
  // a plain browser tab) Office.context.mailbox is undefined. Bail gracefully
  // rather than throwing "Cannot read properties of undefined".
  if (!handlerInstalled && mailbox?.addHandlerAsync) {
    const refresh = async () => {
      const items = await getEffectiveSelection();
      addBreadcrumb({
        category: 'office.event',
        message: 'selection-refresh',
        data: { count: items.length },
      });
      notify(items);
    };
    // Multi-select changes in the message list.
    mailbox.addHandlerAsync(Office.EventType.SelectedItemsChanged, refresh, (res) => {
      if (res.status === Office.AsyncResultStatus.Succeeded) {
        handlerInstalled = true;
      }
    });
    // Single-item read surface: fires when the displayed message changes so the
    // fallback in getEffectiveSelection stays in sync.
    if (Office.EventType.ItemChanged) {
      mailbox.addHandlerAsync(Office.EventType.ItemChanged, refresh, () => {});
    }
  }
  return () => {
    listeners.delete(listener);
  };
}
